import { app, authentication } from '@microsoft/teams-js';
import axios from 'axios';
import { $settings } from '@/provider/settings';
import { getLocale } from '@/plugins/i18n';
import {
  token,
  tokenExpiry,
  setToken,
  setTokenExpiry,
  isExpired,
  setProfile,
  getProfileFromToken,
  getExpiryFromToken,
  setTeamsToken,
  getTeamsToken,
  getTeamsTokenExpiry,
  getTeamsRefreshToken,
} from '@/services/auth/utils/authUtils';
import type {
  AccessToken,
  AuthContext,
  TeamsApp,
  TeamsAuthentication,
} from '@/services/auth/types';
import type { AuthenticationResult } from '@azure/msal-browser';
import localStorageHelper from '@/helper/localStorageHelper';

const teamsApp = app as unknown as TeamsApp;
const teamsAuth = authentication as unknown as TeamsAuthentication;

let teamsAppInitializePromise: Promise<void> | null = null;
let initializePromise: Promise<string | null> | null = null;

const teamsAxios = axios.create({
  withCredentials: true,
});

// Add the same headers that the original interceptors would add
teamsAxios.interceptors.request.use((config) => {
  if (config.url?.includes($settings.middlewareURI)) {
    const cid = localStorageHelper.getCid();
    config.withCredentials = true;

    config.params = {
      ...(config.params as Record<string, unknown>),
      'sap-language': getLocale(),
      'wx-cid': cid,
    };
  }

  return config;
});

const setAuthProfile = (authToken: string) => {
  const profile = getProfileFromToken(authToken);
  if (!profile) return;
  setProfile(profile);
};

const setAuthExpiry = (authToken: string) => {
  const expiryDate = getExpiryFromToken(authToken);
  if (!expiryDate) return;
  setTokenExpiry(expiryDate);
};

/* eslint-disable unicorn/consistent-function-scoping */
export const useAuthTeams = (): AuthContext => {
  const setTeamsAuth = async (): Promise<string | null> => {
    try {
      const teamsToken: string = await teamsAuth.getAuthToken({
        resources: [`${$settings.authentication.teamsScope}`],
        silent: false,
      });
      setAuthProfile(teamsToken);
      setAuthExpiry(teamsToken);
      setToken(teamsToken);

      return teamsToken;
    } catch (error: unknown) {
      console.error('teams getAuthToken failed:', error);
      return null;
    }
  };

  const cacheTeamsInitialization = async (): Promise<string | null> => {
    try {
      if (!teamsAppInitializePromise) {
        teamsAppInitializePromise = teamsApp.initialize();
      }

      await teamsAppInitializePromise;
      const teamsToken = await setTeamsAuth();
      return teamsToken;
    } catch (error: unknown) {
      console.error('teamsApp.initialize failed:', error);
      return null;
    }
  };

  const initialize = async (): Promise<string | null> => {
    const tokenExpired = tokenExpiry.value
      ? isExpired(new Date(tokenExpiry.value))
      : true;

    if (token.value && !tokenExpired) {
      return token.value;
    }

    if (!initializePromise) {
      initializePromise = cacheTeamsInitialization().finally(() => {
        initializePromise = null;
      });
    }

    return initializePromise;
  };

  const exchangeTeamsToken = async (
    bearerToken: string,
    scopes: string,
  ): Promise<AccessToken | null> => {
    if (!bearerToken) {
      return null;
    }

    try {
      const { data: authToken } = await teamsAxios.get<AccessToken>(
        `${$settings.middlewareURI}/token/exchange?scopes=${encodeURIComponent(scopes)}`,
        {
          headers: { authorization: `Bearer ${bearerToken}` },
        },
      );

      return authToken ?? null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getAccessToken = async (
    bearerToken?: string,
  ): Promise<string | null> => {
    const scopes = `${$settings.authentication.audience}/webX`;
    const authToken = bearerToken
      ? await exchangeTeamsToken(bearerToken, scopes)
      : null;

    if (authToken) {
      setTeamsToken(
        authToken.accessToken,
        authToken.expiresIn,
        authToken.refreshToken,
      );
      return authToken.accessToken;
    }

    return null;
  };

  const getRefreshToken = async (
    refreshToken: string,
  ): Promise<string | null> => {
    try {
      const { data: authToken } = await teamsAxios.get<AccessToken>(
        `${$settings.middlewareURI}/token/refresh`,
        {
          headers: {
            authorization: `Bearer ${refreshToken}`,
          },
        },
      );

      if (authToken) {
        setTeamsToken(
          authToken.accessToken,
          authToken.expiresIn,
          authToken.refreshToken,
        );
        return authToken.accessToken;
      }
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getAuthToken = async (): Promise<string | null> => {
    const bearerToken = await initialize();
    if (!bearerToken) {
      return null;
    }

    const expiryDate = getTeamsTokenExpiry();

    if (!expiryDate) {
      return getAccessToken(bearerToken);
    }

    if (isExpired(expiryDate)) {
      try {
        const refreshToken = getTeamsRefreshToken() ?? '';
        return await getRefreshToken(refreshToken);
      } catch {
        return getAccessToken(bearerToken);
      }
    } else {
      return getTeamsToken() ?? '';
    }
  };

  const getGraphToken = async (): Promise<AuthenticationResult | null> => {
    const bearerToken = await initialize();
    if (!bearerToken) {
      return null;
    }

    try {
      const scopes = 'Calendars.Read Calendars.ReadWrite';
      const authToken = await exchangeTeamsToken(bearerToken, scopes);

      if (!authToken) {
        return null;
      }

      const expiresOn = new Date();
      expiresOn.setSeconds(expiresOn.getSeconds() + authToken.expiresIn);

      return {
        accessToken: authToken.accessToken,
        expiresOn,
      } as AuthenticationResult;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getPowerBIToken = async (): Promise<AuthenticationResult | null> => {
    const bearerToken = await initialize();
    if (!bearerToken) {
      return null;
    }

    try {
      const scopes = 'https://analysis.windows.net/powerbi/api/.default';
      const authToken = await exchangeTeamsToken(bearerToken, scopes);

      if (!authToken) {
        return null;
      }

      const expiresOn = new Date();
      expiresOn.setSeconds(expiresOn.getSeconds() + authToken.expiresIn);

      return {
        accessToken: authToken.accessToken,
        expiresOn,
      } as AuthenticationResult;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  initialize().catch(console.error);

  return {
    getAuthToken,
    getGraphToken,
    getPowerBIToken,
  };
};
